import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Même configuration que l'aperçu des factures : le worker vient du CDN, que
// la politique de sécurité autorise explicitement (worker-src).
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Affichage d'un justificatif, image ou PDF, à la largeur qu'on lui laisse.
 *
 * Un seul composant pour les deux usages — l'aperçu plein écran et la
 * comparaison de deux dépenses côte à côte. Les pièges sont les mêmes des deux
 * côtés : un iframe ne suffit pas (Safari sur iPhone n'y affiche pas les PDF,
 * l'écran reste blanc), et un PDF rendu à taille fixe oblige à faire défiler
 * latéralement pour lire un ticket.
 */
export function VisionneuseJustificatif({
  url,
  sombre = false,
  echelle = 1,
  onOuvrir,
}: {
  url: string;
  /** Sur fond sombre, les textes d'attente doivent rester lisibles. */
  sombre?: boolean;
  /** Facteur d'agrandissement, piloté par les boutons de l'aperçu. */
  echelle?: number;
  /** Rend la pièce cliquable pour l'ouvrir en grand. */
  onOuvrir?: () => void;
}) {
  const [type, setType] = useState<'image' | 'pdf' | 'inconnu'>('inconnu');
  const [erreur, setErreur] = useState(false);
  const [pages, setPages] = useState(0);
  const zone = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(0);

  // Les pages sont rendues à la largeur RÉELLE de la zone, mesurée après
  // rendu : en colonne de comparaison, elle ne vaut pas celle de l'écran.
  useEffect(() => {
    const mesurer = () => setLargeur(zone.current?.clientWidth ?? 0);
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [type]);

  useEffect(() => {
    let annule = false;
    setErreur(false);
    // On demande juste les en-têtes : inutile de télécharger deux fois le
    // fichier pour savoir comment l'afficher.
    fetch(url, { method: 'HEAD', credentials: 'same-origin' })
      .then((r) => {
        if (annule) return;
        if (!r.ok) return setErreur(true);
        const mime = r.headers.get('content-type') ?? '';
        setType(mime.startsWith('image/') ? 'image' : mime.includes('pdf') ? 'pdf' : 'inconnu');
      })
      .catch(() => !annule && setErreur(true));
    return () => {
      annule = true;
    };
  }, [url]);

  if (erreur) {
    return (
      <p className="rounded-md bg-white p-4 text-sm text-corail">
        Le justificatif n’a pas pu être chargé. Il est peut-être absent de cette dépense.
      </p>
    );
  }

  const contenu =
    type === 'image' ? (
      // Une image se pince pour zoomer : rien à programmer, le navigateur le
      // fait mieux que nous. L'échelle sert au zoom par boutons, sur
      // ordinateur, où le pincement n'existe pas.
      <img
        src={url}
        alt="Justificatif"
        style={{ width: echelle === 1 ? undefined : `${echelle * 100}%` }}
        className="mx-auto h-auto max-w-full rounded"
      />
    ) : (
      <PagesPdf
        url={url}
        zone={zone}
        largeur={largeur}
        echelle={echelle}
        pages={pages}
        sombre={sombre}
        onPages={setPages}
        onErreur={() => setErreur(true)}
      />
    );

  if (!onOuvrir) return contenu;

  // Cliquer sur la pièce l'ouvre en grand : les vrais justificatifs sont
  // rarement aussi lisibles que ceux d'un jeu de test, et un valideur doit
  // pouvoir approcher un montant illisible sans quitter son parcours.
  return (
    <button
      type="button"
      onClick={onOuvrir}
      title="Agrandir le justificatif"
      className="group relative block w-full cursor-zoom-in"
    >
      {contenu}
      <span className="pointer-events-none absolute right-2 top-2 rounded-md bg-nuit/80 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
        Agrandir
      </span>
    </button>
  );
}

function PagesPdf({
  url,
  zone,
  largeur,
  echelle,
  pages,
  sombre,
  onPages,
  onErreur,
}: {
  url: string;
  zone: React.RefObject<HTMLDivElement | null>;
  largeur: number;
  echelle: number;
  pages: number;
  sombre: boolean;
  onPages: (n: number) => void;
  onErreur: () => void;
}) {
  return (
    <div ref={zone} className="w-full">
      <Document
        file={url}
        onLoadSuccess={({ numPages }) => onPages(numPages)}
        onLoadError={onErreur}
        loading={
          <p className={`p-6 text-center text-sm ${sombre ? 'text-white/70' : 'text-nuit/60'}`}>
            Chargement…
          </p>
        }
      >
        {Array.from({ length: pages }, (_, i) => (
          <Page
            key={i}
            pageNumber={i + 1}
            width={largeur ? largeur * echelle : undefined}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            className="mb-2 rounded bg-white"
          />
        ))}
      </Document>
    </div>
  );
}
